import { forwardRef, useMemo, type ButtonHTMLAttributes } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  PROFILE_FILTER_OPTIONS,
  STATUS_FILTER_OPTIONS,
  TYPE_FILTER_OPTIONS,
} from '@/constants/rbac';
import { useAuth } from '@/contexts/auth';
import { useData } from '@/contexts/data';
import { resolveBrandOptions } from '@/utils/rbac';
import { cn } from '@/lib/utils';

type UsersFiltersProps = {
  type: string;
  roles: string[];
  status: string;
  partnerIds: string[];
  onTypeChange: (_value: string) => void;
  onRolesChange: (_value: string[]) => void;
  onStatusChange: (_value: string) => void;
  onPartnerIdsChange: (_value: string[]) => void;
  onClear: () => void;
  hasActiveFilters: boolean;
};

const FilterButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { label: string; value?: string }
>(({ label, value, className, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cn(
      'inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-lg border border-[#E5E5E5] bg-white px-3 py-2 text-sm font-medium leading-5 text-[#171717] shadow-none hover:bg-gray-50',
      className
    )}
    {...props}
  >
    <span>{value || label}</span>
    <ChevronDown className="h-4 w-4 shrink-0 text-[#171717]" />
  </button>
));
FilterButton.displayName = 'FilterButton';

export const UsersFilters = ({
  type,
  roles,
  status,
  partnerIds,
  onTypeChange,
  onRolesChange,
  onStatusChange,
  onPartnerIdsChange,
  onClear,
  hasActiveFilters,
}: UsersFiltersProps) => {
  const { user } = useAuth();
  const { partners: catalogPartners } = useData();
  const partners = useMemo(
    () => resolveBrandOptions(user, catalogPartners),
    [catalogPartners, user]
  );

  const typeLabel = TYPE_FILTER_OPTIONS.find((option) => option.value === type)?.label;
  const roleLabel =
    roles.length === 0
      ? undefined
      : roles.length === 1
        ? PROFILE_FILTER_OPTIONS.find((option) => option.value === roles[0])?.label
        : `${PROFILE_FILTER_OPTIONS.find((option) => option.value === roles[0])?.label}, ...`;
  const statusLabel = STATUS_FILTER_OPTIONS.find((option) => option.value === status)?.label;
  const selectedPartners = useMemo(
    () => partners.filter((partner) => partnerIds.includes(partner.id)),
    [partners, partnerIds]
  );
  const brandsLabel =
    selectedPartners.length === 0
      ? undefined
      : selectedPartners.length === 1
        ? selectedPartners[0].name
        : `${selectedPartners[0].name}, ${selectedPartners[1]?.name?.slice(0, 4) ?? ''}...`;

  const toggleRole = (role: string) => {
    if (roles.includes(role)) {
      onRolesChange(roles.filter((item) => item !== role));
      return;
    }
    onRolesChange([...roles, role]);
  };

  const togglePartner = (partnerId: string) => {
    if (partnerIds.includes(partnerId)) {
      onPartnerIdsChange(partnerIds.filter((id) => id !== partnerId));
      return;
    }
    onPartnerIdsChange([...partnerIds, partnerId]);
  };

  return (
    <div className="flex flex-row flex-wrap items-center gap-2">
      <Popover modal={false}>
        <PopoverTrigger asChild>
          <FilterButton label="Tipo" value={typeLabel} />
        </PopoverTrigger>
        <PopoverContent align="start" className="z-[80] w-48 p-1">
          {TYPE_FILTER_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className="flex w-full rounded-sm px-3 py-2 text-left text-sm hover:bg-gray-100"
              onClick={() => onTypeChange(option.value === type ? '' : option.value)}
            >
              {option.label}
            </button>
          ))}
        </PopoverContent>
      </Popover>
      <Popover modal={false}>
        <PopoverTrigger asChild>
          <FilterButton label="Perfil" value={roleLabel} />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="z-[80] w-56 p-2"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          {PROFILE_FILTER_OPTIONS.map((option) => (
            <label
              key={option.value}
              className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-gray-50"
              onPointerDown={(event) => event.preventDefault()}
            >
              <Checkbox
                checked={roles.includes(option.value)}
                onCheckedChange={() => toggleRole(option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </PopoverContent>
      </Popover>
      <Popover modal={false}>
        <PopoverTrigger asChild>
          <FilterButton label="Marcas" value={brandsLabel} />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="z-[80] max-h-64 w-64 overflow-y-auto p-2"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          {partners.map((partner) => (
            <label
              key={partner.id}
              className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-gray-50"
              onPointerDown={(event) => event.preventDefault()}
            >
              <Checkbox
                checked={partnerIds.includes(partner.id)}
                onCheckedChange={() => togglePartner(partner.id)}
              />
              <span className="truncate">{partner.name}</span>
            </label>
          ))}
        </PopoverContent>
      </Popover>
      <Popover modal={false}>
        <PopoverTrigger asChild>
          <FilterButton label="Status" value={statusLabel} />
        </PopoverTrigger>
        <PopoverContent align="start" className="z-[80] w-48 p-1">
          {STATUS_FILTER_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className="flex w-full rounded-sm px-3 py-2 text-left text-sm hover:bg-gray-100"
              onClick={() => onStatusChange(option.value === status ? '' : option.value)}
            >
              {option.label}
            </button>
          ))}
        </PopoverContent>
      </Popover>
      {hasActiveFilters && (
        <button
          type="button"
          onClick={onClear}
          className="inline-flex h-9 items-center gap-1 px-2 text-sm font-medium text-[#171717] hover:text-black"
        >
          <X className="h-4 w-4" />
          Limpar filtros
        </button>
      )}
    </div>
  );
};
